import { useCallback, useRef, useState } from 'react'
import { useIdleStatus } from './useIdleStatus'
import { resolveSender } from './inbound'
import { machineNameOf } from './machineName'
import { roomsKey, titleFor, type RoomTitle } from '../chat/roomTitles'
import { listAgents } from '../chatv1/transport'

/** How long a room's roster answer stands before a still-unknown hash may ask again. */
const GUEST_RETRY_MS = 60_000

/**
 * NAMING THE SENDER OF AN ARRIVED MESSAGE — the four resolvers a transcript row
 * needs, and the ref-and-stable-callback discipline that keeps them from
 * repainting the whole log on a timer (M8 cut 4, 2026-09-03).
 *
 * These were four resolvers plus four backing refs woven into TranscriptView's
 * body, each carrying the measured history of why it is a ref read through a
 * zero-dependency callback rather than a value closed over. Moving them here
 * changes no behaviour; it gives the pattern one home and one contract. Every
 * comment below came with its resolver, verbatim.
 */
export function useSenderResolution(args: {
  localMachine?: string | null | undefined
  ownerName?: string | null | undefined
  fleet?: { session: string; hash?: string; device_name?: string; host?: string | null }[] | null | undefined
  rooms?: RoomTitle[] | null | undefined
}) {
  const { localMachine, ownerName, fleet, rooms } = args
  /**
   * Roster lookup for an arrived project message, which names its sender but does
   * not carry their channel hash.
   *
   * Through a ref and a callback with NO dependencies on purpose: the roster is
   * re-fetched every 5s, and a resolver whose identity changed with it would break
   * `Msg`'s memo and re-render every message in the log on a timer — which is the
   * same thing that used to drop the owner's text selection mid-copy. The ref is
   * current by the time any render reads it, and a hash that has only just appeared
   * simply resolves on the next paint.
   */
  const idle = useIdleStatus()
  const roster = useRef<{ session: string; host?: string | null; hash?: string; machine?: string }[]>([])
  roster.current = [...idle.byKey.values()].map((a) => ({
    session: a.session,
    host: a.host ?? null,
    ...(a.hash ? { hash: a.hash } : {}),
    machine: machineNameOf(a, localMachine ?? null),
  }))
  const resolveLabel = useCallback((label: string) => resolveSender(label, roster.current), [])
  // Same ref-and-stable-callback pattern as resolveLabel, for the same reason: a
  // resolver whose identity changed with the poll would re-render every message.
  const localRef = useRef<string | null>(null)
  localRef.current = localMachine ?? null
  const fleetRef = useRef<typeof fleet>(null)
  fleetRef.current = fleet ?? null
  /*
   * Sorted and joined, so ordering churn in the fleet response does not read as a
   * change; ownerName rides along because it is part of what gets rendered. This
   * recomputes per render (cheap string work) but only DIFFERS when an agent appears,
   * disappears, renames, or moves machine — which is when the frozen messages must
   * repaint, and the only time they should.
   */
  const roomsRef = useRef<RoomTitle[]>([])
  roomsRef.current = rooms ?? []
  /** Same ref-and-stable-callback rule as describeHash: the lists behind this
   *  refetch, and a resolver whose identity changed with them would repaint every
   *  message in the log. */
  const describeRoom = useCallback((id: string) => titleFor(roomsRef.current, id), [])
  /*
   * ANOTHER PERSON'S AGENT in a shared room — owner, 2026-10-19: Agent session 不能正確
   * 解析出來 Project Chat 裡其他使用者 Agent 的名稱.
   *
   * The fleet and the idle roster hold THIS account's agents only, so the hash of an agent
   * someone else attached never resolved and its row showed sixteen-odd hex digits. The
   * room's own roster (GET /conversations/{id}/agents, members-only) names every agent
   * attached to it, whoever attached it — the same name Project Chat prints over that
   * agent's message, so the two surfaces agree.
   *
   * Asked lazily, on the first miss, at most once a minute per room, and only for a room
   * this viewer can NAME — a room they are in. Anyone else (a friend reading a shared
   * agent, an owner whose agent sits in a room they left) would only be refused. Names are
   * merged, never replaced: an agent detached after it spoke keeps the name it spoke under.
   */
  const guests = useRef(new Map<string, Map<string, string>>())
  const asked = useRef(new Map<string, number>())
  const [guestRev, setGuestRev] = useState(0)
  const askRoom = useCallback((room: string) => {
    const now = Date.now()
    const last = asked.current.get(room)
    if (last !== undefined && now - last < GUEST_RETRY_MS) return
    asked.current.set(room, now)
    listAgents(room).then(
      ({ agents }) => {
        const known = guests.current.get(room) ?? new Map<string, string>()
        let changed = false
        for (const a of agents) {
          const h = a.agent_hash.toLowerCase()
          const name = (a.name ?? '').trim()
          if (!name || known.get(h) === name) continue
          known.set(h, name)
          changed = true
        }
        guests.current.set(room, known)
        if (changed) setGuestRev((n) => n + 1)
      },
      () => {},
    )
  }, [])
  const resolutionKey =
    (fleet ?? [])
      .map((f) => `${f.hash ?? ''}:${f.session}:${machineNameOf(f, localMachine ?? null)}`)
      .sort()
      .join('|') +
    `§${ownerName ?? ''}` +
    // A room renaming, or the project list simply arriving after the transcript did,
    // must reach the frozen messages — that is the whole job of this key.
    `§${roomsKey(rooms ?? undefined)}` +
    // A guest agent's name arriving from its room's roster.
    `§${guestRev}`
  const describeHash = useCallback((h: string, room?: string | null) => {
    const want = h.toLowerCase()
    /*
     * FLEET FIRST. The idle roster covers only this machine, and the first live test
     * failed on exactly that: test1 lives on the MacBook, was absent from aowl's idle
     * list, and the owner was shown a bare hash under a message that named itself
     * "test1 @ MacBook Pro" one line below. A cross-machine sender is the common case
     * for agent messages — the local list is the supplement, not the source.
     */
    const f = fleetRef.current?.find((r) => r.hash?.toLowerCase() === want)
    if (f) return { name: f.session, machine: machineNameOf(f, localRef.current) }
    const row = roster.current.find((r) => r.hash?.toLowerCase() === want)
    // `machine` is set when the row is built, via machineNameOf — the one place the
    // fallback spelling lives. Re-spelling it here is what machineName.test.ts exists
    // to refuse, and it refused a first draft of this line.
    if (row?.machine) return { name: row.session, machine: row.machine }
    /*
     * Not ours: the room it spoke in may know it. `machine` stays empty — the room's
     * roster says which device, not what its owner calls it, and another person's
     * machine name is theirs to show, not ours to guess.
     */
    if (!room) return null
    const name = guests.current.get(room)?.get(want)
    if (name) return { name, machine: '' }
    if (titleFor(roomsRef.current, room)) askRoom(room)
    return null
  }, [askRoom])

  return { resolveLabel, describeHash, describeRoom, resolutionKey }
}
